/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from 'sharedUi/Button';
import { Toast } from 'sharedUi/Toast';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import PageSkeleton from '../../components/common/page-skeleton';
import { InputSwitch } from 'primereact/inputswitch';
import {
  projectsService,
  type ProjectSettings,
  type DataSourceConfig,
  type AdminAccessConfig,
} from '../../services/projects.service';
import FormInput from '../../components/forms/form-input';
import FormSelect from '../../components/forms/form-select';
import { extractErrorMessage } from '../../utils/error-handler';
import { useCanUpdate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';

interface ProjectSettingsTabProps {
  projectId: string;
  toastRef?: React.RefObject<Toast | null>;
}

/** A credential the server seals and never returns.
 *
 *  The contract is the same for all four and for the Mongo URI: the field starts
 *  blank on every load because the value is write-only, blank means "keep what is
 *  stored", and typing replaces it. Written once rather than four times so a fix to
 *  one cannot leave the other three behind.
 *
 *  Defined at MODULE level, deliberately. Declared inside the tab's body it would be
 *  a new component type on every render, so React would unmount and remount the input
 *  each time state changed — the field would lose focus after a single keystroke and
 *  the credential would be impossible to type.
 */
export const ProjectSettingsTab: React.FC<ProjectSettingsTabProps> = ({
  projectId,
  toastRef,
}) => {
  const queryClient = useQueryClient();
  // Saving settings and testing the data source both write to the project — gate on
  // APP update (the server enforces it too).
  const canUpdate = useCanUpdate(JOB_NAMES.APPS);

  // Local state for settings form
  const [connector, setConnector] = useState<'internal' | 'mongo' | 'api' | 'agent'>('internal');
  const [direction, setDirection] = useState<'inbound' | 'outbound'>('inbound');
  const [custody, setCustody] = useState<'platform' | 'external_vault' | 'ephemeral' | 'none'>('platform');
  const [authStrategy, setAuthStrategy] = useState<'direct_db' | 'passthrough' | 'service_header'>('direct_db');
  const [constantsSource, setConstantsSource] = useState<'seeded' | 'control_db'>('seeded');

  // Mongo specific
  const [mongoUri, setMongoUri] = useState<string>('');  // holds NEW uri entered by user (write-only)
  const [hasUri, setHasUri] = useState<boolean>(false);  // server indicates existing URI is configured
  const [dbName, setDbName] = useState<string>('');
  const [minPoolSize, setMinPoolSize] = useState<number>(1);
  const [maxPoolSize, setMaxPoolSize] = useState<number>(10);
  const [maxIdleTimeMS, setMaxIdleTimeMS] = useState<number>(60000);
  const [serverTimeoutMS, setServerTimeoutMS] = useState<number>(5000);
  const [socketTimeoutMS, setSocketTimeoutMS] = useState<number>(20000);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  // Break-glass access
  const [breakGlass, setBreakGlass] = useState<'disabled' | 'active' | 'requested'>('disabled');
  const [breakGlassReason, setBreakGlassReason] = useState<string>('');

  // Authentication methods (which login methods the project allows)
  const [mpinEnabled, setMpinEnabled] = useState<boolean>(false);
  const [passwordEnabled, setPasswordEnabled] = useState<boolean>(false);
  // RBAC: whether this project uses an org-unit hierarchy. false → unit-less project
  // (unitModel: 'none'): personnel and role bindings are created without a unit.
  const [usesUnits, setUsesUnits] = useState<boolean>(true);
  const [googleEnabled, setGoogleEnabled] = useState<boolean>(false);
  const [googleClientId, setGoogleClientId] = useState<string>('');
  const [googleSecret, setGoogleSecret] = useState<string>('');   // write-only (raw)
  const [googleHasSecret, setGoogleHasSecret] = useState<boolean>(false);
  const [googleDomains, setGoogleDomains] = useState<string>(''); // comma-separated
  const [msEnabled, setMsEnabled] = useState<boolean>(false);
  const [msClientId, setMsClientId] = useState<string>('');
  const [msSecret, setMsSecret] = useState<string>('');           // write-only (raw)
  const [msHasSecret, setMsHasSecret] = useState<boolean>(false);
  const [msTenantId, setMsTenantId] = useState<string>('common');
  const [msDomains, setMsDomains] = useState<string>('');         // comma-separated

  // Test state
  const [testResult, setTestResult] = useState<{
    success: boolean;
    status: string;
    latencyMs?: number | null;
    message: string;
    testedAt: string;
    error?: string | null;
  } | null>(null);

  // Tracks the profileGeneration the editable form was last hydrated from, so we
  // can tell an actual load/save (generation bumps) apart from a Test-Connection
  // refetch (generation unchanged) and avoid clobbering unsaved edits.
  const hydratedGenRef = useRef<number | null>(null);

  // Fetch current project settings
  const { data: settings, isLoading, refetch } = useQuery({
    queryKey: ['project-settings', projectId],
    queryFn: async () => await projectsService.getSettings(projectId),
    enabled: !!projectId,
  });

  // Sync state when settings are loaded
  useEffect(() => {
    if (!settings) return;
    const ds = settings.dataSource || ({} as DataSourceConfig);

    // Keep the "last tested" banner in sync regardless of what triggered the change.
    if (ds.lastTestedAt) {
      setTestResult({
        success: ds.status === 'ACTIVE',
        status: ds.status,
        message: ds.status === 'ACTIVE' ? 'Last tested successfully' : (ds.lastError || 'Test failed'),
        testedAt: ds.lastTestedAt,
        error: ds.lastError,
      });
    }

    // Only (re)hydrate the editable fields on first load or after a real save.
    // A "Test Connection" refetches settings WITHOUT bumping profileGeneration; if we
    // re-hydrated there, setMongoUri('') would blank the write-only URI field (and any
    // other unsaved edits) that the user is about to Save — which persisted settings
    // with hasUri:false. Gating on the generation preserves in-progress edits.
    const gen = settings.profileGeneration ?? 0;
    if (hydratedGenRef.current === gen) return;
    hydratedGenRef.current = gen;

    setConnector(ds.connector || 'internal');
    setDirection(ds.direction || 'inbound');
    setCustody(ds.custody || 'platform');
    setAuthStrategy(ds.authStrategy || 'direct_db');
    setConstantsSource(ds.constantsSource || 'seeded');

    if (ds.mongo) {
      // The server never returns the raw URI for security — only hasUri (boolean).
      // Clear any previously typed URI so the field acts as a write-only "change URI" input.
      setHasUri(ds.mongo.hasUri ?? false);
      setMongoUri('');  // always start blank; user must re-type to change
      setDbName(ds.mongo.dbName || '');
      setMinPoolSize(ds.mongo.minPoolSize ?? 1);
      setMaxPoolSize(ds.mongo.maxPoolSize ?? 10);
      setMaxIdleTimeMS(ds.mongo.maxIdleTimeMS ?? 60000);
      setServerTimeoutMS(ds.mongo.serverSelectionTimeoutMS ?? 5000);
      setSocketTimeoutMS(ds.mongo.socketTimeoutMS ?? 20000);
    }

    const aa = settings.adminAccess || ({} as AdminAccessConfig);
    setBreakGlass(aa.breakGlass || 'disabled');
    setBreakGlassReason(aa.activeReason || '');

    // Default to "uses units" when unset, matching the backend default of 'required'.
    setUsesUnits((settings.rbac?.unitModel ?? 'required') !== 'none');

    const authMethods = settings.auth?.methods;
    if (authMethods) {
      setMpinEnabled(!!authMethods.mpin?.enabled);
      setPasswordEnabled(!!authMethods.password?.enabled);

      const g = authMethods.google;
      setGoogleEnabled(!!g?.enabled);
      setGoogleClientId(g?.clientId || '');
      setGoogleHasSecret(!!g?.hasClientSecret);
      setGoogleSecret('');
      setGoogleDomains((g?.allowedDomains || []).join(', '));

      const m = authMethods.microsoft;
      setMsEnabled(!!m?.enabled);
      setMsClientId(m?.clientId || '');
      setMsHasSecret(!!m?.hasClientSecret);
      setMsSecret('');
      setMsTenantId(m?.tenantId || 'common');
      setMsDomains((m?.allowedDomains || []).join(', '));
    }
  }, [settings]);

  // Split a comma/space separated domain list into a clean string[].
  const parseDomains = (raw: string): string[] =>
    raw.split(/[\s,]+/).map((d) => d.trim().toLowerCase()).filter(Boolean);

  // Update Settings Mutation
  const saveMutation = useMutation({
    mutationFn: async (payload: Partial<ProjectSettings>) => {
      const updated = await projectsService.updateSettings(projectId, payload);
      // The update endpoint returns the raw (unmasked) settings — it may carry uriRef
      // and won't include the write-only `hasUri` flag. Whether a URI is now stored is
      // derivable from the payload we just sent (new rawUri) or an existing uriRef.
      const sentNewUri = Boolean((payload as any)?.dataSource?.mongo?.rawUri?.trim?.());
      return { updated, sentNewUri };
    },
    onSuccess: ({ updated, sentNewUri }) => {
      // Seed the cache with a normalized copy so the form reflects saved state
      // immediately — independent of whether the background GET refetch is stale/cached.
      const rawMongo = (updated?.dataSource?.mongo ?? {}) as any;
      const hasUri = sentNewUri || Boolean(rawMongo.uriRef || rawMongo.hasUri);
      const normalized: ProjectSettings = {
        ...updated,
        dataSource: {
          ...updated.dataSource,
          mongo: updated.dataSource?.mongo
            ? // strip the write-only secret ref, expose only the hasUri boolean
              (() => {
                const rest: Record<string, any> = {};
                for (const [k, v] of Object.entries(rawMongo)) {
                  if (k !== 'uriRef' && k !== 'rawUri') rest[k] = v;
                }
                return { ...rest, hasUri };
              })()
            : updated.dataSource?.mongo,
        },
      };
      queryClient.setQueryData(['project-settings', projectId], normalized);

      // Reflect the "URI Configured" badge right away for a freshly saved URI.
      if (hasUri) setHasUri(true);

      // Reconcile with the server in the background (also refreshes the read-only view).
      queryClient.invalidateQueries({ queryKey: ['project-settings', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
      toastRef?.current?.show({
        severity: 'success',
        summary: 'Settings Saved',
        detail: `Project settings updated (Generation ${updated.profileGeneration}).`,
        life: 3000,
      });
    },
    onError: (err: any) => {
      toastRef?.current?.show({
        severity: 'error',
        summary: 'Save Failed',
        detail: extractErrorMessage(err, 'Failed to update project settings'),
        life: 10000,
      });
    },
  });

  // Test Connection Mutation
  const testMutation = useMutation({
    mutationFn: async () => {
      const payload: any = {
        connector,
        serverSelectionTimeoutMS: serverTimeoutMS,
      };
      if (connector === 'mongo') {
        // Always send as rawUri for the test endpoint
        if (mongoUri.trim()) {
          payload.rawUri = mongoUri.trim();
        }
        payload.dbName = dbName || undefined;
      }
      return await projectsService.testDataSource(projectId, payload);
    },
    onSuccess: (res) => {
      setTestResult(res);
      refetch();
      toastRef?.current?.show({
        severity: res.success ? 'success' : 'warn',
        summary: res.success ? 'Connection Healthy' : 'Connection Error',
        detail: res.message,
        life: 5000,
      });
    },
    onError: (err: any) => {
      const msg = extractErrorMessage(err, 'Connection test failed');
      setTestResult({
        success: false,
        status: 'ERROR',
        message: msg,
        testedAt: new Date().toISOString(),
        error: msg,
      });
      toastRef?.current?.show({
        severity: 'error',
        summary: 'Test Failed',
        detail: msg,
        life: 10000,
      });
    },
  });

  const handleSave = () => {
    // The background-job range pre-check that used to live here went with the
    // fields it guarded: those tunables are edited in Application Configuration
    // now, so validating them before a payload that no longer carries them would
    // block a save on a value this screen cannot change.
    const payload: any = {
      dataSource: {
        connector,
        direction,
        custody,
        authStrategy,
        constantsSource,
        status: settings?.dataSource?.status || 'UNCONFIGURED',
        lifecycle: settings?.dataSource?.lifecycle || 'created',
      },
      // `backgroundJobs` and `integrations` are NOT sent. Both moved to
      // Application Configuration, which has its own screen, and this endpoint
      // now REFUSES a payload carrying either — deliberately, because the
      // alternative was a save that reported success and changed nothing: the
      // values it wrote were no longer the ones anything read.
      //
      // They are still RENDERED above, read-only, sourced from where they now
      // live, so this screen keeps showing what the send path actually resolves.
      adminAccess: {
        breakGlass,
        activeReason: breakGlassReason || null,
      },
      rbac: {
        unitModel: usesUnits ? 'required' : 'none',
      },
      auth: {
        schemaVersion: 1,
        methods: {
          mpin: { enabled: mpinEnabled },
          password: { enabled: passwordEnabled },
          google: {
            enabled: googleEnabled,
            clientId: googleClientId || null,
            allowedDomains: parseDomains(googleDomains),
            // Only send a raw secret when the admin typed a new one (sealed server-side).
            ...(googleSecret.trim() ? { rawClientSecret: googleSecret.trim() } : {}),
          },
          microsoft: {
            enabled: msEnabled,
            clientId: msClientId || null,
            tenantId: msTenantId.trim() || 'common',
            allowedDomains: parseDomains(msDomains),
            ...(msSecret.trim() ? { rawClientSecret: msSecret.trim() } : {}),
          },
        },
      },
    };

    if (connector === 'mongo') {
      const mongoObj: any = {
        dbName: dbName || null,
        minPoolSize,
        maxPoolSize,
        maxIdleTimeMS,
        serverSelectionTimeoutMS: serverTimeoutMS,
        socketTimeoutMS,
      };
      // Only send rawUri when the user has entered a new connection string
      if (mongoUri.trim()) {
        mongoObj.rawUri = mongoUri.trim();
      }
      payload.dataSource.mongo = mongoObj;
    }

    saveMutation.mutate(payload);
  };

  if (isLoading) {
    return (
      <PageSkeleton />
    );
  }

  const dsStatus = settings?.dataSource?.status || 'UNCONFIGURED';
  const lifecycle = settings?.dataSource?.lifecycle || 'created';

  return (
    <div className="space-y-6" data-testid="SCR-Project-Settings-Tab">
      {/* 1. Data Source Identity & 5-Axis Configuration */}
      <div className="ds-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-3 border-b border-line">
          <div>
            <h2 className="text-base font-semibold text-ink flex items-center gap-2">
              <i className="pi pi-database text-primary-500" />
              Data Source Configuration (5-Axis Model)
            </h2>
            <p className="text-xs text-ink-muted mt-0.5">
              Defines tenant data isolation, physical connectivity, and encryption custody.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Tag
              value={dsStatus}
              severity={
                dsStatus === 'ACTIVE' ? 'success' : dsStatus === 'ERROR' ? 'danger' : 'secondary'
              }
            />
            <Tag value={`Lifecycle: ${lifecycle}`} severity="info" />
            {settings?.profileGeneration && (
              <Tag value={`Gen ${settings.profileGeneration}`} severity="warning" />
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormSelect
            name="connector"
            label="Connector Type"
            value={connector}
            onChange={(e) => setConnector(e.target.value as any)}
            options={[
              { value: 'internal', label: 'Control Database (Shared Platform)' },
              { value: 'mongo', label: 'Dedicated MongoDB (Tenant Database)' },
              { value: 'api', label: 'External HTTP API (Stage 8)' },
              { value: 'agent', label: 'Sovereign Customer Agent (Stage 8)' },
            ]}
          />

          <FormSelect
            name="direction"
            label="Direction"
            value={direction}
            onChange={(e) => setDirection(e.target.value as any)}
            options={[
              { value: 'inbound', label: 'Inbound (Platform dials Tenant)' },
              { value: 'outbound', label: 'Outbound (Tenant Agent dials Platform)' },
            ]}
          />

          <FormSelect
            name="custody"
            label="Secret Custody"
            value={custody}
            onChange={(e) => setCustody(e.target.value as any)}
            options={[
              { value: 'platform', label: 'Platform KMS Envelope (aes://)' },
              { value: 'external_vault', label: 'External Key Vault' },
              { value: 'ephemeral', label: 'Ephemeral / Short-lived' },
              { value: 'none', label: 'None (Local Custody)' },
            ]}
          />

          <FormSelect
            name="authStrategy"
            label="Auth Strategy"
            value={authStrategy}
            onChange={(e) => setAuthStrategy(e.target.value as any)}
            options={[
              { value: 'direct_db', label: 'Direct Database Auth' },
              { value: 'passthrough', label: 'Passthrough JWT / Header' },
              { value: 'service_header', label: 'Service Header / mTLS' },
            ]}
          />

          <FormSelect
            name="constantsSource"
            label="Constants Source"
            value={constantsSource}
            onChange={(e) => setConstantsSource(e.target.value as any)}
            options={[
              { value: 'seeded', label: 'Seeded Rulebook in Tenant DB' },
              { value: 'control_db', label: 'Control Plane DB Direct' },
            ]}
          />
        </div>

        {/* MongoDB Dedicated Configuration */}
        {connector === 'mongo' && (
          <div className="mt-5 pt-4 border-t border-line bg-canvas dark:bg-gray-900/50 p-4 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
              <i className="pi pi-server text-emerald-500" />
              MongoDB Connection & Pool Guardrails
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    Connection String (URI)
                  </label>
                  <div className="flex items-center gap-2">
                    {hasUri && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" /></svg>
                        URI Configured
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-xs text-primary-600 hover:underline"
                    >
                      {showPassword ? 'Hide' : hasUri ? 'Change URI' : 'Show'}
                    </button>
                  </div>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={mongoUri}
                  onChange={(e) => setMongoUri(e.target.value)}
                  placeholder={hasUri
                    ? 'Enter a new URI to replace the existing one...'
                    : 'mongodb://user:pass@host:27017/?authSource=admin'}
                  className="w-full px-3 py-2 text-xs font-mono rounded border border-gray-300 dark:border-gray-600 bg-surface text-ink"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  {hasUri
                    ? 'A URI is already stored (sealed). Leave blank to keep it, or enter a new one to replace.'
                    : 'Plaintext URI will be sealed into AES secret store with AAD bound to this project.'}
                </p>
              </div>

              <FormInput
                name="dbName"
                label="Database Name"
                value={dbName}
                onChange={(e) => setDbName(e.target.value)}
                placeholder="e.g. project_dhp_prod"
                helperText="Tenant database namespace on the MongoDB cluster"
              />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 pt-2">
              <FormInput
                name="minPoolSize"
                label="Min Pool"
                type="number"
                value={String(minPoolSize)}
                onChange={(e) => setMinPoolSize(parseInt(e.target.value, 10) || 1)}
                helperText="Idle warm conn"
              />
              <FormInput
                name="maxPoolSize"
                label="Max Pool"
                type="number"
                value={String(maxPoolSize)}
                onChange={(e) => setMaxPoolSize(parseInt(e.target.value, 10) || 10)}
                helperText="Bounded upper limit"
              />
              <FormInput
                name="maxIdleTimeMS"
                label="Max Idle (ms)"
                type="number"
                value={String(maxIdleTimeMS)}
                onChange={(e) => setMaxIdleTimeMS(parseInt(e.target.value, 10) || 60000)}
                helperText="Eviction TTL"
              />
              <FormInput
                name="serverTimeoutMS"
                label="Timeout (ms)"
                type="number"
                value={String(serverTimeoutMS)}
                onChange={(e) => setServerTimeoutMS(parseInt(e.target.value, 10) || 5000)}
                helperText="Server selection"
              />
              <FormInput
                name="socketTimeoutMS"
                label="Socket (ms)"
                type="number"
                value={String(socketTimeoutMS)}
                onChange={(e) => setSocketTimeoutMS(parseInt(e.target.value, 10) || 20000)}
                helperText="Socket timeout"
              />
            </div>
          </div>
        )}

        {/* Live Test Action Bar */}
        <div className="mt-4 pt-3 flex flex-wrap items-center justify-between gap-3">
          {canUpdate && (
            <Button
              label={testMutation.isPending ? 'Testing Connection...' : 'Test Connection'}
              icon={testMutation.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-bolt'}
              severity="secondary"
              outlined
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending || saveMutation.isPending}
            />
          )}

          {testResult && (
            <div className="flex items-center gap-2 text-xs">
              <i
                className={`pi ${
                  testResult.success ? 'pi-check-circle text-emerald-500' : 'pi-times-circle text-rose-500'
                }`}
              />
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {testResult.message}
              </span>
              {testResult.latencyMs && (
                <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-[11px] text-gray-600 dark:text-gray-300">
                  {testResult.latencyMs} ms
                </span>
              )}
            </div>
          )}
        </div>

        {testResult && !testResult.success && testResult.error && (
          <div className="mt-3">
            <Message severity="error" text={testResult.error} className="w-full text-xs" />
          </div>
        )}
      </div>

      {/* Background jobs and channel integrations used to live here. They are
          Application Configuration now — one typed, audited row per setting, with
          its own screen — so this tab holds only what is genuinely a property of
          the PROJECT: where its data lives, and who may break glass into it.

          A pointer rather than a read-only copy of the values: two screens showing
          the same settings is how they come to disagree, and the one that cannot
          be edited is the one that goes stale. */}
      <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-1 flex items-center gap-2 text-base font-semibold text-ink">
          <i className="pi pi-sliders-h text-indigo-500" />
          Background Jobs &amp; Channel Integrations
        </h2>
        <p className="mb-3 text-xs text-ink-muted">
          These moved to Application Configuration. Every value is typed, versioned and
          audited there, and a project can override a platform default or fall back to it
          without a release.
        </p>
        <a
          href="/app-config"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:underline dark:text-indigo-400"
          data-testid="ProjectSettings.Link.AppConfig"
        >
          Open Application Configuration
          <i className="pi pi-arrow-right text-xs" />
        </a>
      </div>

      {/* 4. Break-Glass Administration & Compliance */}
      <div className="ds-card p-5">
        <h2 className="text-base font-semibold text-ink flex items-center gap-2 mb-1">
          <i className="pi pi-shield text-amber-500" />
          Administrative Break-Glass Access (SOC2 / ISO Governance)
        </h2>
        <p className="text-xs text-ink-muted mb-4">
          Time-bound, reason-tracked emergency support access into tenant database.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormSelect
            name="breakGlass"
            label="Break-Glass Status"
            value={breakGlass}
            onChange={(e) => setBreakGlass(e.target.value as any)}
            options={[
              { value: 'disabled', label: 'Disabled (Strict Tenant Isolation)' },
              { value: 'active', label: 'Active (Emergency Access Enabled)' },
              { value: 'requested', label: 'Requested (Pending Tenant Consent)' },
            ]}
          />

          <FormInput
            name="breakGlassReason"
            label="Active Reason / Ticket Ref"
            value={breakGlassReason}
            onChange={(e) => setBreakGlassReason(e.target.value)}
            placeholder="e.g. INC-8921 Production Data Recovery"
            disabled={breakGlass === 'disabled'}
          />
        </div>
      </div>

      {/* 5. Access Control (RBAC) */}
      <div className="ds-card p-5">
        <h2 className="text-base font-semibold text-ink flex items-center gap-2 mb-1">
          <i className="pi pi-sitemap text-indigo-500" />
          Access Control
        </h2>
        <p className="text-xs text-ink-muted mb-4">
          Whether this project uses an org-unit hierarchy. Turn this off for projects that
          have no units — personnel and role bindings are then created without a unit, and
          permissions apply globally within the project.
        </p>
        <div className="flex items-center gap-3">
          <InputSwitch checked={usesUnits} onChange={(e: any) => setUsesUnits(e.value)} />
          <div>
            <label className="text-sm text-gray-700 dark:text-gray-300">Uses org units</label>
            <p className="text-xs text-ink-muted">
              {usesUnits
                ? 'Unit is required on personnel and role bindings.'
                : 'Unit-less project: personnel and role bindings omit the unit.'}
            </p>
          </div>
        </div>
      </div>

      {/* 6. Authentication Methods */}
      <div className="ds-card p-5">
        <h2 className="text-base font-semibold text-ink flex items-center gap-2 mb-1">
          <i className="pi pi-lock text-rose-500" />
          Authentication Methods
        </h2>
        <p className="text-xs text-ink-muted mb-4">
          Which login methods this project allows. OAuth providers use this project's own client credentials.
        </p>

        {/* Simple method toggles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <div className="flex items-center gap-2">
            <InputSwitch checked={mpinEnabled} onChange={(e: any) => setMpinEnabled(e.value)} />
            <label className="text-sm text-gray-700 dark:text-gray-300">MPIN</label>
          </div>
          <div className="flex items-center gap-2">
            <InputSwitch checked={passwordEnabled} onChange={(e: any) => setPasswordEnabled(e.value)} />
            <label className="text-sm text-gray-700 dark:text-gray-300">Password</label>
          </div>
          <div className="flex items-center gap-2">
            <InputSwitch checked={googleEnabled} onChange={(e: any) => setGoogleEnabled(e.value)} />
            <label className="text-sm text-gray-700 dark:text-gray-300">Google</label>
          </div>
          <div className="flex items-center gap-2">
            <InputSwitch checked={msEnabled} onChange={(e: any) => setMsEnabled(e.value)} />
            <label className="text-sm text-gray-700 dark:text-gray-300">Microsoft</label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Google provider config */}
          {googleEnabled && (
            <div className="border border-line rounded-lg p-3 bg-gray-50/50 dark:bg-gray-900/30">
              <h3 className="text-xs font-semibold text-ink mb-2 flex items-center gap-1.5">
                <i className="pi pi-google text-red-500" /> Google OAuth
              </h3>
              <div className="space-y-2">
                <FormInput
                  name="googleClientId"
                  label="Client ID"
                  value={googleClientId}
                  onChange={(e) => setGoogleClientId(e.target.value)}
                  placeholder="xxxx.apps.googleusercontent.com"
                />
                <FormInput
                  name="googleSecret"
                  type="password"
                  label="Client Secret"
                  value={googleSecret}
                  onChange={(e) => setGoogleSecret(e.target.value)}
                  placeholder={googleHasSecret ? 'Stored — leave blank to keep' : 'Enter client secret'}
                  helperText={googleHasSecret ? 'A secret is sealed. Enter a new one to replace.' : 'Sealed into the secret store on save.'}
                />
                <FormInput
                  name="googleDomains"
                  label="Allowed Email Domains"
                  value={googleDomains}
                  onChange={(e) => setGoogleDomains(e.target.value)}
                  placeholder="example.com, gov.in (blank = any)"
                />
              </div>
            </div>
          )}

          {/* Microsoft provider config */}
          {msEnabled && (
            <div className="border border-line rounded-lg p-3 bg-gray-50/50 dark:bg-gray-900/30">
              <h3 className="text-xs font-semibold text-ink mb-2 flex items-center gap-1.5">
                <i className="pi pi-microsoft text-blue-500" /> Microsoft (Entra ID)
              </h3>
              <div className="space-y-2">
                <FormInput
                  name="msClientId"
                  label="Client ID"
                  value={msClientId}
                  onChange={(e) => setMsClientId(e.target.value)}
                  placeholder="Application (client) ID"
                />
                <FormInput
                  name="msSecret"
                  type="password"
                  label="Client Secret"
                  value={msSecret}
                  onChange={(e) => setMsSecret(e.target.value)}
                  placeholder={msHasSecret ? 'Stored — leave blank to keep' : 'Enter client secret'}
                  helperText={msHasSecret ? 'A secret is sealed. Enter a new one to replace.' : 'Sealed into the secret store on save.'}
                />
                <FormInput
                  name="msTenantId"
                  label="Tenant / Directory"
                  value={msTenantId}
                  onChange={(e) => setMsTenantId(e.target.value)}
                  placeholder="common"
                  helperText="'common' = any Microsoft org; a GUID = single organization only"
                />
                <FormInput
                  name="msDomains"
                  label="Allowed Email Domains"
                  value={msDomains}
                  onChange={(e) => setMsDomains(e.target.value)}
                  placeholder="example.com (blank = any)"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save Action Bar */}
      {canUpdate && (
        <div className="flex justify-end gap-3 pt-2">
          <Button
            label={saveMutation.isPending ? 'Saving Settings...' : 'Save Settings'}
            icon={saveMutation.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
            onClick={handleSave}
            disabled={saveMutation.isPending || testMutation.isPending}
          />
        </div>
      )}
    </div>
  );
};
export default ProjectSettingsTab;
